import React from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, ArrowRight } from 'lucide-react';

const WA_LINK = "https://chat.whatsapp.com/HxBfbxuBabo9BVEYJPbj3c?mode=gi_t";
const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/0aefe824-dd3a-42e7-88bf-f336a3227281/images/00da63ae31cf506759794c8fc45a8779d07141ee920e8173270980d9fe26aada.png";

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (i) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.18, duration: 0.8, ease: [0.22, 1, 0.36, 1] }
  })
};

export default function HeroSection() {
  const scrollToLookbook = () => document.getElementById('lookbook')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section data-testid="hero-section" className="relative min-h-screen bg-[#F5F0E8] overflow-hidden flex items-center">
      {/* Background half-panel */}
      <div className="absolute top-0 right-0 w-full md:w-1/2 h-full bg-[#E8DED1]" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-0 items-stretch min-h-screen w-full">
        {/* Left — Text */}
        <div className="flex flex-col justify-center py-32 md:py-28 pr-0 md:pr-12 order-2 md:order-1">
          <motion.span
            custom={0} variants={fadeUp} initial="hidden" animate="visible"
            className="inline-block text-xs md:text-sm font-sans uppercase tracking-[0.25em] text-[#C9A84C] font-medium mb-6"
          >
            India's Finest Handcraft
          </motion.span>

          <motion.h1
            custom={1} variants={fadeUp} initial="hidden" animate="visible"
            className="font-serif font-light text-[#2D4A2D] text-4xl sm:text-5xl lg:text-[3.5rem] xl:text-6xl leading-[1.08] tracking-tight mb-6"
          >
            Where every thread<br />
            <span className="italic text-[#2D4A2D]">tells a story</span>
          </motion.h1>

          <motion.p
            custom={2} variants={fadeUp} initial="hidden" animate="visible"
            className="font-sans font-light text-[#5C6B5C] text-base md:text-lg leading-relaxed mb-10 max-w-md"
          >
            Lucknow Chikankari — woven with love, patience aur thodi si
            jaadui kala. Har tukda authentically handcrafted, sirf aapke liye.
          </motion.p>

          <motion.div
            custom={3} variants={fadeUp} initial="hidden" animate="visible"
            className="flex flex-col sm:flex-row gap-4"
          >
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-whatsapp"
              className="flex items-center justify-center gap-2.5 bg-[#2D4A2D] text-[#F5F0E8] px-8 py-4 font-sans font-medium text-sm tracking-wide transition-all duration-300 hover:bg-[#1A2A1A] hover:text-[#C9A84C]"
            >
              <MessageCircle size={18} strokeWidth={1.5} />
              Join Our Community
            </a>
            <button
              onClick={scrollToLookbook}
              data-testid="hero-cta-lookbook"
              className="flex items-center justify-center gap-2.5 border border-[#2D4A2D] text-[#2D4A2D] px-8 py-4 font-sans font-medium text-sm tracking-wide transition-all duration-300 hover:bg-[#2D4A2D] hover:text-[#F5F0E8] group"
            >
              Explore Lookbook
              <ArrowRight size={18} strokeWidth={1.5} className="group-hover:translate-x-1 transition-transform duration-200" />
            </button>
          </motion.div>

          {/* Stats row */}
          <motion.div
            custom={4} variants={fadeUp} initial="hidden" animate="visible"
            className="flex gap-8 mt-12 pt-8 border-t border-[#D5CCBF]"
          >
            {[
              ['36+', 'Hand stitch types'],
              ['Natural', 'Fabrics only'],
              ['500+', 'Happy customers'],
            ].map(([num, label], i) => (
              <React.Fragment key={label}>
                {i > 0 && <div className="w-px bg-[#D5CCBF]" />}
                <div>
                  <div className="font-serif text-2xl text-[#2D4A2D] font-light leading-none mb-1">{num}</div>
                  <div className="text-xs font-sans text-[#5C6B5C] uppercase tracking-wider leading-tight">{label}</div>
                </div>
              </React.Fragment>
            ))}
          </motion.div>
        </div>

        {/* Right — Image */}
        <motion.div
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
          className="order-1 md:order-2 relative overflow-hidden h-[55vh] md:h-auto"
        >
          <img
            src={HERO_IMG}
            alt="Woman in handcrafted Chikankari"
            className="w-full h-full object-cover object-center"
            loading="eager"
          />
          {/* Floating tag */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.2, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="absolute bottom-8 left-6 bg-[#F5F0E8]/95 backdrop-blur-md px-5 py-3.5 border border-[#C9A84C]/40 shadow-lg"
          >
            <div className="text-[10px] font-sans uppercase tracking-[0.25em] text-[#C9A84C] font-semibold mb-1">New Collection</div>
            <div className="font-serif text-[#1A2A1A] text-xl font-medium">Bahar 2026</div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
